import get from 'lodash.get'
import { appLocalStorage } from '../utils/misc'
import {
  IDENTITY_CONF_OIDC_METADATA_VALUES_REQUESTED,
  IDENTITY_SERVER_CONFIG_VALUES,
  LOCAL_STORAGE_APP_CONTEXT_DATA_VAR_NAME,
  LOCAL_STORAGE_DOWNLOAD_MANAGER_KEY,
  LOCAL_STORAGE_USER_SESSION_VAR_NAME
} from '../utils/constants'
import { IIdentitySigninRedirectStateValues, IUserSession } from './interface'
import { Log, User, UserManager, WebStorageStateStore } from 'oidc-client'

const appendOktaProviderToAuthorizeEndpoint = (metadata: any) => {
  if (!metadata || !metadata.authorization_endpoint) return metadata

  const params = new URLSearchParams(window.location.search)
  const provider = params.get('external_provider')?.toLowerCase()

  const allowedProviders = ['okta-conekta', 'okta-kueski']
  if (!provider || !allowedProviders.includes(provider)) return metadata

  const authEndpoint = metadata.authorization_endpoint
  const isAuthorizeEndpoint = authEndpoint.endsWith('/connect/authorize')
  if (!isAuthorizeEndpoint) return metadata

  const separator = authEndpoint.includes('?') ? '&' : '?'
  return {
    ...metadata,
    authorization_endpoint: `${authEndpoint}${separator}external_provider=${encodeURIComponent(provider)}`
  }
}

export class AuthService {
  private static _instance: AuthService | null = null

  public static getInstance = (): AuthService => {
    if (!AuthService._instance) {
      AuthService._instance = new AuthService()
    }
    return AuthService._instance
  }
  public userManager: UserManager

  constructor() {
    const metadata = { ...IDENTITY_CONF_OIDC_METADATA_VALUES_REQUESTED }
    const modifiedMetadata = appendOktaProviderToAuthorizeEndpoint(metadata)

    this.userManager = new UserManager({
      ...IDENTITY_SERVER_CONFIG_VALUES,
      metadata: modifiedMetadata,
      userStore: new WebStorageStateStore({ store: window.localStorage })
    })
    if (process.env.NODE_ENV !== 'production') {
      Log.logger = console
      Log.level = Log.INFO
    }

    // this.userManager.events.addAccessTokenExpired(() => {})
    this.userManager.startSilentRenew()
    // this.userManager.events.addSilentRenewError((e: any) => {})

    this.userManager.events.addUserLoaded((user: User) => {
      this.setSessionInfo(user)
    })
    this.userManager.events.addUserUnloaded(() => {
      this.removeSessionInfo()
    })
  }

  createSigninRequest = () => {
    this.userManager.createSigninRequest()
  }

  getUser = async (): Promise<User | null> => {
    const USER = await this.userManager.getUser()
    if (!USER) {
      return await this.userManager.signinRedirectCallback()
    }
    return USER
  }

  hasTokenExpired = () => {
    return new Date((appLocalStorage.getValue(LOCAL_STORAGE_USER_SESSION_VAR_NAME).expires_at || 0) * 1000) < new Date()
  }

  isAuthenticated = () => {
    return appLocalStorage.getValue(LOCAL_STORAGE_USER_SESSION_VAR_NAME).access_token || false
  }

  logout = () => {
    appLocalStorage.removeItem(LOCAL_STORAGE_DOWNLOAD_MANAGER_KEY)
    appLocalStorage.removeItem(LOCAL_STORAGE_APP_CONTEXT_DATA_VAR_NAME)
    this.userManager.clearStaleState()
    this.userManager.signoutRedirect()
  }

  removeSessionInfo = () => {
    appLocalStorage.removeItem(LOCAL_STORAGE_USER_SESSION_VAR_NAME)
  }

  setSessionInfo = (user: User) => {
    appLocalStorage.setValue(LOCAL_STORAGE_USER_SESSION_VAR_NAME, user)
  }

  signinRedirect = (state: IIdentitySigninRedirectStateValues) =>
    this.userManager.signinRedirect({ data: { location: state } })

  signinRedirectCallback = () =>
    this.userManager
      .signinRedirectCallback()
      .then(({ state }: any) => location.replace(state?.location.from.pathname ?? '/'))
      .catch((err: any) => {
        console.log('err:', err)
        console.log('deviceTime:', new Date().getTime())
        this.removeSessionInfo()
        this.logout()
      })

  signinSilent = () => {
    this.userManager.signinSilent().catch((error: any) => console.log('signinSilent() error:', error))
  }

  getAccessToken = async () => {
    const user = await this.userManager.getUser()
    /* if(user?.expired) {
     user =  await this.userManager.signinSilent()
    } */
    return user?.access_token
  }

  signinSilentCallback = () => {
    this.userManager.signinSilentCallback()
  }
}

export const getToken = (): string =>
  process.env.USE_TOKEN_FROM_STAGING === 'true'
    ? process.env.TOKEN_CREATED_IN_STAGING || ''
    : appLocalStorage.getValue(LOCAL_STORAGE_USER_SESSION_VAR_NAME).access_token || ''

export const getUserSessionDataFromLocalStorage = (): IUserSession =>
  appLocalStorage.getValue(LOCAL_STORAGE_USER_SESSION_VAR_NAME)

export const getUserSessionProfileDataByKey = (profileKey: string): string =>
  get(JSON.parse(get(getUserSessionDataFromLocalStorage(), 'profile.profile', '')), profileKey, '')
